import List from '@/components/common/List';
import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { QueueItem } from '@/types/queue';
import { getUpdatedNodes } from '@/util/application';
import { Button, Stack } from '@mui/material';
import { ConfirmOptions } from 'material-ui-confirm';
import { FC, useState } from 'react';
import QueueListItem from './QueueListItem';
import QueueListItemEdit from './QueueListItemEdit';

type QueuesManagerProps = Record<string, never>;

const QueuesManager: FC<QueuesManagerProps> = () => {
  const { application, setApplication } = useApplication();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();

  const confirmExists: ConfirmOptions = {
    title: 'Queue already exists',
    description: `This queue already exists`,
    confirmationText: 'Ok',
    hideCancelButton: true
  };

  const [openEdit, setOpenEdit] = useState(false);

  const handleOnSave = (newQueue: QueueItem, oldQueue?: string) => {
    let updatedQueues = application.queues ? structuredClone(application.queues) : [];

    if (!oldQueue) {
      updatedQueues[updatedQueues.length] = { ...newQueue };
    } else {
      updatedQueues = updatedQueues.map((queue) => {
        if (queue.queue === oldQueue) {
          return { ...newQueue };
        } else {
          return queue;
        }
      });
    }
    if (
      updatedQueues.filter((queue) => queue.queue.toLocaleLowerCase() === newQueue.queue.toLocaleLowerCase()).length > 1
    ) {
      confirm(confirmExists);
      return;
    }

    setApplication({ ...application, queues: updatedQueues });
    setUpdateNodes(getUpdatedNodes(newQueue.queue, 'queue', application.blocks.details));
  };

  return (
    <Stack gap={1} alignItems='start'>
      <List sx={{ width: '100%' }}>
        {application.queues
          ?.sort((a, b) => a.queue.localeCompare(b.queue))
          .map((queueItem, index) => {
            return (
              <QueueListItem
                key={`prompt.${queueItem.queue}.${index}`}
                queueItem={queueItem}
                onSave={(newQueue) => handleOnSave(newQueue, queueItem.queue)}
                onDelete={() => {
                  const newApplication = application.queues?.filter((newQueue) => newQueue.queue !== queueItem.queue);
                  setApplication({ ...application, queues: newApplication });
                  setUpdateNodes(getUpdatedNodes(queueItem.queue, 'queue', application.blocks.details));
                }}
              />
            );
          })}
      </List>
      {openEdit && (
        <QueueListItemEdit
          onClose={() => {
            setOpenEdit(false);
          }}
          onSave={(newUserEvent) => handleOnSave(newUserEvent)}
        />
      )}
      <Button variant='contained' color='primary' sx={{ ml: 2 }} onClick={() => setOpenEdit(true)}>
        Add new
      </Button>
    </Stack>
  );
};

export default QueuesManager;
